import { usersApi } from "#/api/usersApi";
import ActionButton from "#/components/buttons/ActionButton";
import BaseTable from "#/components/tables/BaseTable";
import type { BulkCreateUsersPayload, User } from "#/types/user";
import { ClientUserPermission } from "#/types/user";
import { isValidEmail, sanitize } from "#/utils/StringUtils";
import { logger } from "#/utils/logger";
import {
  Button,
  Drawer,
  type DrawerProps,
  Select,
  Stepper,
  Table,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconConfetti,
  IconDeviceFloppy,
  IconDownload,
  IconPlus,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import Papa from "papaparse";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

export interface Props extends DrawerProps {
  clientId: number;
  onSuccess: () => void;
  mode?: "manual" | "csv";
}

const ImportUsersDrawer = ({ clientId, onSuccess, mode = "csv", ...props }: Props) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [active, setActive] = useState<number>(0);
  const nextStep = () =>
    setActive((current) => (current < 3 ? current + 1 : current));

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [usersPayload, setUsersPayload] = useState<BulkCreateUsersPayload>();
  const [saving, setSaving] = useState<boolean>(false);

  // Initialize with empty row for manual mode
  useEffect(() => {
    if (props.opened) {
      // Reset stepper
      setActive(0);

      if (mode === "manual") {
        // Start fresh with one empty row for manual mode
        setUsersPayload({
          users: [{ name: "", email: "", clientId, clientPermission: ClientUserPermission.FULL }],
        });
      } else if (mode === "csv") {
        // Clear payload for CSV mode
        setUsersPayload(undefined);
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  }, [props.opened, mode, clientId]);
  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/templates/users_creation_template.csv";
    link.download = "users_creation_template.csv"; // Change filename here
    link.click();
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const jsonData = (results.data as Partial<User>[]).map((item) => {
            // Parse permission, handle case-insensitive values
            let permission = ClientUserPermission.FULL;
            if (item.clientPermission) {
              const permValue = item.clientPermission.toLowerCase();
              if (permValue === 'read') permission = ClientUserPermission.READ;
              else if (permValue === 'write') permission = ClientUserPermission.WRITE;
              else if (permValue === 'full') permission = ClientUserPermission.FULL;
            }

            return {
              name: item.name || "",
              email: item.email || "",
              clientId: clientId,
              clientPermission: permission,
            };
          });
          setUsersPayload({
            users: jsonData,
          });
          nextStep();
        },
      });
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const createUsers = useCallback(async () => {
    if (!usersPayload) return;
    setSaving(true);
    try {
      await usersApi.bulkCreate(usersPayload);
      setSaving(false);
      nextStep();
    } catch (error) {
      logger.error("ImportUsersDrawer createUsers error:", error);
      setSaving(false);
    }
  }, [usersPayload]);

  const addNewRow = () => {
    if (!usersPayload) return;
    setUsersPayload({
      users: [...usersPayload.users, { name: "", email: "", clientId, clientPermission: ClientUserPermission.FULL }],
    });
  };

  const updateUser = (index: number, field: "name" | "email" | "clientPermission", value: string | ClientUserPermission) => {
    if (!usersPayload) return;
    const updatedUsers = [...usersPayload.users];
    updatedUsers[index] = { ...updatedUsers[index], [field]: value };
    setUsersPayload({ users: updatedUsers });
  };

  const deleteUser = (index: number) => {
    if (!usersPayload) return;
    setUsersPayload({
      users: usersPayload.users.filter((_, i) => i !== index),
    });
  };

  const hasValidUsers = () => {
    if (!usersPayload?.users || usersPayload.users.length === 0) return false;
    return usersPayload.users.some(user =>
      user.name.trim() !== "" &&
      user.email.trim() !== "" &&
      isValidEmail(user.email)
    );
  };

  return (
    <Drawer
      {...props}
      position={"bottom"}
      title={mode === "manual" ? t("clients.importUsers.addManuallyTitle") : t("clients.importUsers.importTitle")}
      size={"90%"}
      closeOnClickOutside={false}
      onClose={() => {
        // Reset everything when drawer closes
        setActive(0);
        setUsersPayload(undefined);
        setSaving(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        props.onClose();
      }}
    >
      <div className="w-full flex flex-col">
        <Stepper
          active={active}
          onStepClick={setActive}
          allowNextStepsSelect={false}
          color={"green"}
          size="xs"
          iconSize={32}
        >
          {mode === "csv" && (
            <Stepper.Step
              label={t("clients.importUsers.uploadCsv")}
              description={t("clients.importUsers.uploadDescription")}
            >
            <div className="w-full h-full grid grid-rows-2 gap-4">
              <div className="w-full flex h-48 items-center flex-col gap-5 bg-gray-50 border border-gray-300 rounded-lg justify-center">
                <Title order={6}>{t("clients.importUsers.downloadTemplate")}</Title>
                <p className="text-sm">
                  {t("clients.importUsers.downloadTemplateInstruction")}
                </p>
                <Button
                  color="black"
                  size="sm"
                  leftSection={<IconDownload size={18} />}
                  onClick={downloadTemplate}
                >
                  {t("clients.importUsers.downloadTemplate")}
                </Button>
              </div>
              <div className="w-full flex h-48 items-center flex-col gap-5 bg-gray-50 border border-gray-300 rounded-lg justify-center">
                <Title order={6}>{t("clients.importUsers.uploadCsvSection")}</Title>
                <p className="text-sm">
                  {t("clients.importUsers.uploadCsvDescription")}
                </p>
                <div>
                  <Button
                    color="black"
                    size="sm"
                    variant="outline"
                    leftSection={<IconUpload size={18} />}
                    onClick={triggerFileInput}
                  >
                    {t("clients.importUsers.uploadUsersCsv")}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                  />
                </div>
              </div>
            </div>
          </Stepper.Step>
          )}
          <Stepper.Step
            label={mode === "manual" ? t("clients.importUsers.addUsersTab") : t("clients.importUsers.verify")}
            description={mode === "manual" ? t("clients.importUsers.addUsersDescription") : t("clients.importUsers.verifyDescription")}
          >
            {usersPayload && (
              <div className="w-full flex flex-col gap-4">
                <BaseTable
                  headers={[t("common.name"), t("common.email"), t("common.permission"), t("common.actions")]}
                  rows={usersPayload?.users?.map((item, index) => {
                    return (
                      <Table.Tr key={index}>
                        <Table.Td>
                          <TextInput
                            value={item.name}
                            onChange={(e) => updateUser(index, "name", e.currentTarget.value)}
                            placeholder={t("clients.importUsers.enterName")}
                            size="sm"
                          />
                        </Table.Td>
                        <Table.Td>
                          <TextInput
                            value={item.email}
                            onChange={(e) => updateUser(index, "email", e.currentTarget.value)}
                            placeholder={t("clients.importUsers.enterEmail")}
                            type="email"
                            size="sm"
                            error={item.email && !isValidEmail(item.email)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Select
                            value={item.clientPermission || ClientUserPermission.FULL}
                            onChange={(value) => updateUser(index, "clientPermission", value as ClientUserPermission)}
                            data={Object.values(ClientUserPermission).map((permission) => ({
                              label: sanitize(permission),
                              value: permission,
                            }))}
                            size="sm"
                            placeholder={t("clients.importUsers.selectPermission")}
                          />
                        </Table.Td>
                        <Table.Td className="w-24">
                          <div className="w-full flex items-center justify-center">
                            <PermissionTooltip disabled={!canWrite()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="outline"
                                color="red"
                                onClick={() => deleteUser(index)}
                                disabled={!canWrite()}
                              />
                            </PermissionTooltip>
                          </div>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                />
                <div className="w-full flex items-center justify-between">
                  <PermissionTooltip disabled={!canWrite()}>
                    <Button
                      leftSection={<IconPlus size={18} />}
                      variant="outline"
                      color="black"
                      onClick={addNewRow}
                      size="sm"
                      disabled={!canWrite()}
                    >
                      {t("clients.importUsers.addRow")}
                    </Button>
                  </PermissionTooltip>
                  <PermissionTooltip disabled={!canWrite()}>
                    <Button
                      leftSection={<IconDeviceFloppy size={18} />}
                      color="black"
                      onClick={createUsers}
                      loading={saving}
                      disabled={!hasValidUsers() || !canWrite()}
                    >
                      {t("common.save")}
                    </Button>
                  </PermissionTooltip>
                </div>
              </div>
            )}
          </Stepper.Step>
          <Stepper.Completed>
            <div className="w-full flex flex-col items-center justify-center gap-2 py-10">
              <div className="bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 inline-block p-5 rounded-full">
                <IconConfetti size={60} stroke={1} color="white" />
              </div>

              <div className="inline-block text-transparent bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 bg-clip-text">
                <Title>{t("common.success")}</Title>
              </div>
              <span>
                {t("clients.importUsers.usersCreatedDescription")}
              </span>
              <Button
                color="black"
                onClick={() => {
                  setActive(0);
                  setUsersPayload(undefined);
                  setSaving(false);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                  onSuccess();
                }}
              >
                {t("common.close")}
              </Button>
            </div>
          </Stepper.Completed>
        </Stepper>
      </div>
    </Drawer>
  );
};

export default ImportUsersDrawer;
